<template>
    <div class="automation-trigger-settings manual-trigger">
        <p>
            {{ t('automations-form-triggers_config-manual-description') }}
        </p>

        <div class="field">
            <label class="label">
                {{ t('automations-form-triggers_config-manual-fields_label') }}
                <br />
                <small>{{ t('automations-form-triggers_config-manual-fields_help') }}</small>
            </label>

            <div
                v-for="(field, index) in fields"
                :key="index"
                class="manual-field-row"
                :class="{ 'has-error': visibleFieldErrors[index] }"
            >
                <div class="manual-field-main">
                    <input
                        type="text"
                        class="manual-field-name"
                        v-model="field.key"
                        :placeholder="t('automations-form-triggers_config-manual-name_placeholder')"
                        autocapitalize="off"
                        autocorrect="off"
                        spellcheck="false"
                        @input="sanitizeFieldKey(index)"
                        @blur="field.touched = true"
                    />

                    <select class="manual-field-type" v-model="field.type">
                        <option value="text">{{ t('automations-form-triggers_config-manual-types-text') }}</option>
                        <option value="number">{{ t('automations-form-triggers_config-manual-types-number') }}</option>
                        <option value="date">{{ t('automations-form-triggers_config-manual-types-date') }}</option>
                        <option value="boolean">{{ t('automations-form-triggers_config-manual-types-boolean') }}</option>
                    </select>

                    <label class="manual-switch" :class="{ 'is-on': field.required }">
                        <input type="checkbox" v-model="field.required" />
                        <span class="manual-switch-track"><span class="manual-switch-thumb"></span></span>
                        <span class="manual-switch-text">
                            {{ t('automations-form-triggers_config-manual-required_label') }}
                        </span>
                    </label>

                    <button
                        type="button"
                        class="manual-field-remove"
                        @click.prevent="removeField(index)"
                        :title="t('automations-form-triggers_config-manual-remove_field_title')"
                    >
                        <i class="fa fa-trash"></i>
                    </button>
                </div>

                <div
                    class="manual-field-footer"
                    v-if="visibleFieldErrors[index] || field.key"
                >
                    <span class="manual-field-error" v-if="visibleFieldErrors[index]">
                        <i class="fa fa-exclamation-circle"></i>
                        {{ visibleFieldErrors[index] }}
                    </span>
                    <button
                        type="button"
                        class="manual-field-snippet"
                        v-else-if="field.key"
                        :title="t('automations-form-triggers_config-manual-copy_snippet_title')"
                        @click.prevent="copyToken('[[campos.' + field.key + ']]')"
                    >
                        <!-- i18n-validator-ignore-next-line: snippet preview -->
                        <code>[[campos.{{ field.key }}]]</code>
                        <i class="fa fa-copy"></i>
                    </button>
                </div>
            </div>

            <button
                type="button"
                class="manual-add-field"
                @click.prevent="addField"
            >
                <i class="fa fa-plus"></i>
                {{ t('automations-form-triggers_config-manual-add_field_button') }}
            </button>
        </div>

        <div class="field mt-4">
            <div class="api-toggle" @click="apiOpen = !apiOpen">
                <i :class="apiOpen ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                <strong>{{ t('automations-form-triggers_config-manual-api_title') }}</strong>
            </div>

            <div v-if="apiOpen" class="api-section mt-3">
                <small v-if="!automationId">
                    {{ t('automations-form-triggers_config-manual-api_unsaved') }}
                </small>
                <template v-else>
                    <small>{{ t('automations-form-triggers_config-manual-api_help') }}</small>
                    <p class="api-docs-link mt-2 mb-0">
                        <a
                            href="https://gokevi.notion.site/?pvs=74"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {{ t('automations-form-triggers_config-manual-api_docs_link') }}
                            <i class="fa fa-external-link"></i>
                        </a>
                    </p>
                    <pre class="api-example">{{ curlExample }}</pre>
                    <button class="btn btn-sm btn-outline-primary" @click.prevent="copyToken(curlExample)">
                        <i class="fa fa-copy"></i>
                        {{ t('automations-form-triggers_config-manual-api_copy_button') }}
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="less">
.manual-trigger {
    @accent: #004d26;

    .manual-field-row {
        margin-top: 8px;
        padding: 10px 12px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #fff;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;

        &:hover {
            border-color: #d1d5db;
        }

        &.has-error {
            border-color: #dc3545;
            background: #fff8f8;
        }
    }

    .manual-field-main {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;
    }

    .manual-field-name,
    .manual-field-type {
        height: 36px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        padding: 0 10px;
        background: #fff;
        color: #111827;
        font-size: 14px;

        &:focus {
            outline: none;
            border-color: @accent;
            box-shadow: 0 0 0 3px rgba(0, 77, 38, 0.12);
        }
    }

    .manual-field-name {
        flex: 1 1 160px;
        min-width: 140px;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;

        &::placeholder {
            font-family: inherit;
            color: #9ca3af;
        }
    }

    .manual-field-type {
        width: 130px;
        flex: none;
        cursor: pointer;
    }

    .manual-switch {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        padding: 0 4px;
        white-space: nowrap;
        font-weight: normal;
        font-size: 13px;
        color: #6b7280;
        cursor: pointer;
        user-select: none;

        input {
            position: absolute;
            opacity: 0;
            width: 0;
            height: 0;
        }

        .manual-switch-track {
            position: relative;
            display: block;
            width: 36px;
            height: 20px;
            flex: none;
            border-radius: 999px;
            background: #d1d5db;
            transition: background 0.18s ease;
        }

        .manual-switch-thumb {
            position: absolute;
            top: 2px;
            left: 2px;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #fff;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
            transition: transform 0.18s ease;
        }

        input:focus-visible + .manual-switch-track {
            box-shadow: 0 0 0 3px rgba(0, 77, 38, 0.18);
        }

        &.is-on {
            color: @accent;
            font-weight: 500;

            .manual-switch-track {
                background: @accent;
            }

            .manual-switch-thumb {
                transform: translateX(16px);
            }
        }
    }

    .manual-field-remove {
        width: 36px;
        height: 36px;
        flex: none;
        border: 1px solid transparent;
        border-radius: 6px;
        background: transparent;
        color: #9ca3af;
        transition: all 0.15s ease;

        &:hover {
            border-color: #f5c2c7;
            background: #fdf2f2;
            color: #dc3545;
        }
    }

    .manual-field-footer {
        margin-top: 6px;
    }

    .manual-field-error {
        font-size: 12px;
        color: #dc3545;

        i {
            margin-right: 4px;
        }
    }

    .manual-field-snippet {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        background: transparent;
        padding: 0;
        font-size: 12px;
        color: #6b7280;

        code {
            background: #f3f4f6;
            border-radius: 4px;
            padding: 2px 6px;
            color: #374151;
        }

        &:hover {
            color: @accent;
        }
    }

    .manual-add-field {
        margin-top: 10px;
        height: 36px;
        padding: 0 14px;
        border: 1px dashed #d1d5db;
        border-radius: 6px;
        background: transparent;
        color: #4b5563;
        font-size: 14px;
        transition: all 0.15s ease;

        i {
            margin-right: 6px;
        }

        &:hover {
            border-color: @accent;
            color: @accent;
            background: rgba(0, 77, 38, 0.04);
        }
    }

    .api-toggle {
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        gap: 8px;
        color: #555;
        &:hover {
            color: #333;
        }
        i {
            font-size: 11px;
            width: 12px;
        }
    }
    .api-section {
        border-left: 3px solid #e0e0e0;
        padding-left: 16px;
    }
    .api-docs-link {
        a {
            color: @accent;
            font-size: 13px;
            text-decoration: none;

            i {
                margin-left: 4px;
                font-size: 11px;
            }

            &:hover {
                text-decoration: underline;
            }
        }
    }
    .api-example {
        background: #f5f5f5;
        padding: 12px;
        border-radius: 4px;
        font-size: 12px;
        white-space: pre-wrap;
        word-break: break-all;
    }
}
</style>

<script>
import keviApi from "../../../keviApi";

export default {
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        automationId: {
            default: null,
        },
    },
    data: function () {
        return {
            fields: this.parseFields(this.value),
            apiOpen: false,
        };
    },
    computed: {
        validFields() {
            return this.fields.filter((field) => field.key);
        },
        fieldErrors() {
            const errors = {};
            const seen = {};
            this.fields.forEach((field, index) => {
                if (!field.key) {
                    errors[index] = this.t("automations-form-triggers_config-manual-error_key_required");
                    return;
                }
                if (seen[field.key]) {
                    errors[index] = this.t("automations-form-triggers_config-manual-error_key_duplicated");
                    return;
                }
                seen[field.key] = true;
            });
            return errors;
        },
        // A freshly added row is invalid but shouldn't be painted red before
        // the user has had a chance to name it.
        visibleFieldErrors() {
            const visible = {};
            Object.keys(this.fieldErrors).forEach((index) => {
                if (this.fields[index] && this.fields[index].touched) {
                    visible[index] = this.fieldErrors[index];
                }
            });
            return visible;
        },
        persistedFields() {
            return this.fields.map((field) => ({
                key: field.key,
                type: field.type,
                required: !!field.required,
            }));
        },
        curlExample() {
            const body = {
                automation_id: this.automationId,
                client: {
                    id: "id-externo-do-cliente",
                    email: "cliente@exemplo.com",
                    phone: "+5511999999999",
                    name: "Nome do cliente",
                },
                fields: this.exampleFields,
            };
            const endpoint = keviApi.getApiEndpoint("web/automations/manual");
            return [
                `curl -X POST ${endpoint} \\`,
                `  -H "Authorization: Bearer SEU_TOKEN" \\`,
                `  -H "Content-Type: application/json" \\`,
                `  -d '${JSON.stringify(body, null, 2)}'`,
            ].join("\n");
        },
        exampleFields() {
            const example = {};
            this.validFields.forEach((field) => {
                switch (field.type) {
                    case "number":
                        example[field.key] = 10;
                        break;
                    case "date":
                        example[field.key] = "2026-01-31";
                        break;
                    case "boolean":
                        example[field.key] = true;
                        break;
                    default:
                        example[field.key] = "valor";
                }
            });
            return example;
        },
    },
    mounted: function () {
        window.setTimeout(() => {
            this.changed();
        }, 100);
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        parseFields(value) {
            const conditions = Array.isArray(value) && value.length > 0 ? value[0] : {};
            const fields = conditions && Array.isArray(conditions.fields) ? conditions.fields : [];
            return fields.map((field) => ({
                key: field.key || "",
                type: field.type || "text",
                required: !!field.required,
                touched: true,
            }));
        },
        addField() {
            this.fields.push({ key: "", type: "text", required: false, touched: false });
        },
        removeField(index) {
            this.fields.splice(index, 1);
        },
        sanitizeFieldKey(index) {
            this.fields[index].key = this.fields[index].key
                // Decompose accents into base letter + diacritic so the
                // diacritics can be stripped instead of dropping the letter.
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, "");
        },
        copyToken(token) {
            navigator.clipboard.writeText(token);
        },
        isValid() {
            return Object.keys(this.fieldErrors).length === 0;
        },
        changed() {
            this.$emit("input", [{ fields: this.persistedFields }]);
        },
    },
    watch: {
        fields: {
            handler: function () {
                this.changed();
            },
            deep: true,
        },
        value: function (value) {
            const parsed = this.parseFields(value);
            // Compare only the persisted shape; `touched` is local UI state and
            // would otherwise make every self-emitted change look external.
            const incoming = parsed.map(({ key, type, required }) => ({ key, type, required }));
            if (JSON.stringify(incoming) !== JSON.stringify(this.persistedFields)) {
                this.fields = parsed;
            }
        },
    },
};
</script>
